/**
 * Camp Contribution Service
 * 
 * Automatically promotes user programs to community database
 * Handles matching, enhancing, and creating camp entries
 */

import { supabase } from '@/lib/supabase';
import { 
  sanitizeProgramDataForSharing, 
  validateNoPII,
  parseScheduleText 
} from './privacyService';
import { UserProgram } from '@/api/entities';

/**
 * Automatically promote user program to community database
 * Called whenever a user adds or updates a program
 * 
 * @param {string} userProgramId - UUID of user_programs record
 * @param {string} userId - UUID of user
 * @returns {Object} Result of promotion {action, campId, errors}
 */
export async function autoPromoteUserProgramToCommunity(userProgramId, userId) {
  try {
    // 1. Check user's sharing preference (they can opt out in Settings)
    const { data: profile } = await supabase
      .from('profiles')
      .select('share_camp_data_with_community, children, full_name')
      .eq('id', userId)
      .single();
    
    // User has explicitly opted out
    if (profile?.share_camp_data_with_community === false) {
      console.log('[Auto-promote] User opted out of sharing');
      return { 
        action: 'skipped', 
        reason: 'user_opted_out' 
      };
    }
    
    // 2. Fetch user program from user_programs table
    let userProgram;
    try {
      userProgram = await UserProgram.get(userProgramId);
      if (!userProgram) {
        const error = new Error('Program not found');
        error.code = 'PGRST116';
        console.error('[Auto-promote] Failed to fetch user program:', error);
        return { action: 'error', error };
      }
    } catch (fetchError) {
      console.error('[Auto-promote] Failed to fetch user program:', fetchError);
      return { action: 'error', error: fetchError };
    }
    
    // 3. Sanitize data (remove PII)
    console.log('[Auto-promote] DEBUG - Raw program_data:', JSON.stringify(userProgram.program_data, null, 2));
    const sanitizedData = sanitizeProgramDataForSharing(
      userProgram.program_data,
      profile
    );
    console.log('[Auto-promote] DEBUG - Sanitized data:', JSON.stringify(sanitizedData, null, 2));
    
    // 4. Validate no PII leaked
    const validation = validateNoPII(sanitizedData, profile);
    if (!validation.safe) {
      console.warn('[Auto-promote] PII validation warnings:', validation.issues);
      // Continue but flag for review
      sanitizedData._validation_warnings = validation.issues;
    }
    
    // 5. Check if this camp already exists - FIRST try direct link, then fuzzy matching
    let existingCamp = null;
    
    // Strategy 1: Direct link via program_data.id or program_data.place_id
    const programId = userProgram.program_data?.id || userProgram.program_data?.place_id;
    if (programId) {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(programId);
      
      let directQuery = supabase.from('camp_audit_data').select('id, name, location, coordinates_lat, coordinates_lng, place_id');
      
      if (isUUID) {
        directQuery = directQuery.eq('id', programId);
      } else {
        directQuery = directQuery.eq('place_id', programId);
      }
      
      const { data: directMatch, error: directError } = await directQuery.single();
      
      if (!directError && directMatch) {
        existingCamp = directMatch;
        console.log('[Auto-promote] Found direct match via ID/place_id:', programId);
      }
    }
    
    // Strategy 2: Fall back to fuzzy matching if no direct link
    if (!existingCamp) {
      existingCamp = await findMatchingCamp(
        sanitizedData.name,
        sanitizedData.location,
        sanitizedData.latitude,
        sanitizedData.longitude
      );
      if (existingCamp) {
        console.log('[Auto-promote] Found fuzzy match:', existingCamp.id);
      }
    }
    
    let result;
    
    if (existingCamp) {
      // 6a. Enhance existing camp with user data
      result = await enhanceExistingCamp(existingCamp.id, sanitizedData, userId, userProgram);
      
      // Link back to user program - update program_data with the camp_audit_data ID
      // This ensures future edits use the direct link
      if (userProgram.program_data) {
        userProgram.program_data.id = existingCamp.id;
        userProgram.program_data.place_id = existingCamp.place_id || userProgram.program_data.place_id;
        // Update the user program with the link
        try {
          await UserProgram.update(userProgramId, {
            program_data: userProgram.program_data
          });
        } catch (updateError) {
          console.warn('[Auto-promote] Failed to update program_data link:', updateError);
          // Don't fail the whole operation
        }
      }
      
      console.log('[Auto-promote] Enhanced camp', existingCamp.id, 'for program', userProgramId);
      
      result.action = 'enhanced';
    } else {
      // 6b. Create new camp entry
      result = await createCampFromSanitizedData(sanitizedData, userId, userProgram);
      
      // Link back to user program - update program_data with the new camp_audit_data ID
      if (userProgram.program_data && result.campId) {
        userProgram.program_data.id = result.campId;
        // Update the user program with the link
        try {
          await UserProgram.update(userProgramId, {
            program_data: userProgram.program_data
          });
        } catch (updateError) {
          console.warn('[Auto-promote] Failed to update program_data link:', updateError);
          // Don't fail the whole operation
        }
      }
      
      console.log('[Auto-promote] Created camp', result.campId, 'for program', userProgramId);
      
      result.action = 'created';
    }
    
    console.log('[Auto-promote] Success:', result);
    return result;
    
  } catch (error) {
    console.error('[Auto-promote] Error:', error);
    
    // Log failure (could update program_data with promotion_status if needed)
    console.error('[Auto-promote] Failed to promote program', userProgramId, ':', error);
    
    return { action: 'error', error };
  }
}

/**
 * Find matching camp in camp_audit_data using fuzzy matching
 */
async function findMatchingCamp(name, location, lat, lng) {
  if (!name) return null;
  
  // Strategy 1: Try exact or very similar name match
  const { data: nameMatches } = await supabase
    .from('camp_audit_data')
    .select('id, name, location, coordinates_lat, coordinates_lng, place_id')
    .ilike('name', `%${name.substring(0, 20)}%`) // Fuzzy name match
    .limit(10);
  
  if (!nameMatches || nameMatches.length === 0) {
    return null;
  }
  
  // Strategy 2: If we have coordinates, find closest geographic match
  if (lat && lng) {
    const matches = nameMatches.map(camp => ({
      ...camp,
      distance: calculateDistance(
        lat, lng,
        camp.coordinates_lat, camp.coordinates_lng
      ),
      nameMatch: calculateNameSimilarity(name, camp.name)
    }));
    
    // Filter: Within 1 mile and name similarity > 70%
    const closeMatches = matches.filter(m => 
      m.distance < 1.0 && m.nameMatch > 0.7
    );
    
    if (closeMatches.length > 0) {
      // Return closest match
      return closeMatches.sort((a, b) => a.distance - b.distance)[0];
    }
  }
  
  // Strategy 3: Location text match (if no coordinates)
  if (location) {
    const locationMatches = nameMatches.filter(camp => {
      if (!camp.location) return false;
      const campLoc = camp.location.toLowerCase();
      const userLoc = location.toLowerCase();
      
      // Check if locations share city or significant substring
      return campLoc.includes(userLoc) || userLoc.includes(campLoc);
    });
    
    if (locationMatches.length > 0) {
      return locationMatches[0];
    }
  }
  
  return null; // No confident match found
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
  
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate name similarity (simple Levenshtein-based ratio)
 */
function calculateNameSimilarity(name1, name2) {
  const s1 = name1.toLowerCase().trim();
  const s2 = name2.toLowerCase().trim();
  
  if (s1 === s2) return 1.0;
  
  // Simple ratio: shared words / total unique words
  const words1 = new Set(s1.split(/\s+/));
  const words2 = new Set(s2.split(/\s+/));
  
  const intersection = [...words1].filter(w => words2.has(w));
  const union = new Set([...words1, ...words2]);
  
  return intersection.length / union.size;
}

/**
 * Enhance existing camp with user-contributed data
 */
async function enhanceExistingCamp(campAuditId, sanitizedData, userId, userProgram) {
  console.log('[Auto-promote] DEBUG - enhanceExistingCamp called with:');
  console.log('  - campAuditId:', campAuditId);
  console.log('  - sanitizedData:', JSON.stringify(sanitizedData, null, 2));
  console.log('  - userProgram.source:', userProgram.source);
  
  const enhancements = {};
  const contributions = [];
  
  // Extract structured fields from sanitized data
  
  // 1. Dates
  if (sanitizedData.schedule_dates?.start && sanitizedData.schedule_dates?.end) {
    enhancements.user_camp_start_date = sanitizedData.schedule_dates.start;
    enhancements.user_camp_end_date = sanitizedData.schedule_dates.end;
    enhancements.dates_confidence = 'medium';
    
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'camp_dates',
      new_value: sanitizedData.schedule_dates,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  // 1b. Important dates
  if (sanitizedData.registration_open_date) {
    enhancements.registration_open_date = sanitizedData.registration_open_date;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'registration_open_date',
      new_value: sanitizedData.registration_open_date,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  if (sanitizedData.early_registration_open_date) {
    enhancements.early_registration_open_date = sanitizedData.early_registration_open_date;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'early_registration_open_date',
      new_value: sanitizedData.early_registration_open_date,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  if (sanitizedData.registration_deadline) {
    enhancements.registration_deadline = sanitizedData.registration_deadline;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'registration_deadline',
      new_value: sanitizedData.registration_deadline,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  if (sanitizedData.early_bird_deadline) {
    enhancements.early_bird_deadline = sanitizedData.early_bird_deadline;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'early_bird_deadline',
      new_value: sanitizedData.early_bird_deadline,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  if (sanitizedData.cancellation_deadline) {
    enhancements.cancellation_deadline = sanitizedData.cancellation_deadline;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'cancellation_deadline',
      new_value: sanitizedData.cancellation_deadline,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  // 2. Cost
  if (sanitizedData.price) {
    enhancements.user_cost_details = {
      amount: sanitizedData.price,
      per: sanitizedData.price_period || 'week',
      notes: sanitizedData.price_notes,
      reported_date: new Date().toISOString()
    };
    enhancements.cost_confidence = 'medium';
    
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'cost',
      new_value: enhancements.user_cost_details,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75
    });
  }
  
  // 3. Schedule
  if (sanitizedData.schedule_text) {
    const parsedSchedule = parseScheduleText(sanitizedData.schedule_text);
    
    enhancements.user_schedule_details = parsedSchedule;
    enhancements.schedule_confidence = 'medium';
    
    // Extract session times if available
    if (parsedSchedule?.drop_off) {
      enhancements.user_session_start_time = parsedSchedule.drop_off;
    }
    if (parsedSchedule?.pick_up) {
      enhancements.user_session_end_time = parsedSchedule.pick_up;
    }
    
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'schedule',
      new_value: parsedSchedule,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 65
    });
  }
  
  // 3b. Session times (direct fields, if schedule_text not available)
  if (sanitizedData.session_start_time) {
    enhancements.user_session_start_time = sanitizedData.session_start_time;
    if (!enhancements.user_schedule_details) {
      enhancements.user_schedule_details = {};
    }
    if (!enhancements.user_schedule_details.drop_off) {
      enhancements.user_schedule_details.drop_off = sanitizedData.session_start_time;
    }
    enhancements.schedule_confidence = 'medium';
  }
  
  if (sanitizedData.session_end_time) {
    enhancements.user_session_end_time = sanitizedData.session_end_time;
    if (!enhancements.user_schedule_details) {
      enhancements.user_schedule_details = {};
    }
    if (!enhancements.user_schedule_details.pick_up) {
      enhancements.user_schedule_details.pick_up = sanitizedData.session_end_time;
    }
    enhancements.schedule_confidence = 'medium';
  }
  
  // 4. Activities
  if (sanitizedData.activities && sanitizedData.activities.length > 0) {
    enhancements.user_activities = sanitizedData.activities;
    
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'activities',
      new_value: { activities: sanitizedData.activities },
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 80
    });
  }
  
  // 5. Contact information
  // Use base contact_email and contact_phone columns from schema
  // If they don't exist, the update will skip them but continue with other fields
  if (sanitizedData.email) {
    enhancements.contact_email = sanitizedData.email;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'contact_email',
      new_value: sanitizedData.email,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75
    });
  }
  
  if (sanitizedData.phone) {
    enhancements.contact_phone = sanitizedData.phone;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'contact_phone',
      new_value: sanitizedData.phone,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75
    });
  }
  
  if (sanitizedData.website) {
    enhancements.website_url = sanitizedData.website;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'website_url',
      new_value: sanitizedData.website,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75
    });
  }
  
  // 6. Refund policy
  if (sanitizedData.refund_policy) {
    enhancements.refund_policy = sanitizedData.refund_policy;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'refund_policy',
      new_value: sanitizedData.refund_policy,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70
    });
  }
  
  // 0. Basic information (organization and description)
  if (sanitizedData.organization) {
    enhancements.organization = sanitizedData.organization;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'organization',
      new_value: sanitizedData.organization,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 80
    });
  }

  // Description - use editorial_summary (which exists) instead of brief_description
  // editorial_summary is the column that exists in the database
  if (sanitizedData.description || sanitizedData.brief_description) {
    const descriptionText = sanitizedData.description || sanitizedData.brief_description;
    // Only use editorial_summary since brief_description column may not exist
    enhancements.editorial_summary = descriptionText;
    contributions.push({
      camp_audit_id: campAuditId,
      user_id: userId,
      field_name: 'description',
      new_value: descriptionText,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75
    });
  }
  
  // Get current values for increment operations
  const { data: currentCamp } = await supabase
    .from('camp_audit_data')
    .select('community_contribution_count, data_confidence_score')
    .eq('id', campAuditId)
    .single();
  
  // Debug logging before update
  console.log('[Auto-promote] DEBUG - Enhancements object:', JSON.stringify(enhancements, null, 2));
  console.log('[Auto-promote] DEBUG - Number of enhancements:', Object.keys(enhancements).length);
  console.log('[Auto-promote] DEBUG - Contributions array:', JSON.stringify(contributions, null, 2));
  
  // Filter out fields that might not exist in the database
  // Build update object with only safe fields first
  const safeUpdateFields = {
    has_community_enhancements: true,
    community_contribution_count: (currentCamp?.community_contribution_count || 0) + 1,
    last_community_update: new Date().toISOString(),
    data_confidence_score: Math.min((currentCamp?.data_confidence_score || 0) + 5, 95)
  };
  
  // Add enhancements, but filter out contact fields if they cause errors
  // We'll try with contact fields first, and if it fails, retry without them
  const updateWithContact = {
    ...safeUpdateFields,
    ...enhancements
  };
  
  // Update camp_audit_data with enhancements
  let { error: updateError } = await supabase
    .from('camp_audit_data')
    .update(updateWithContact)
    .eq('id', campAuditId);
  
  // If update failed due to missing columns, retry without them
  if (updateError && updateError.code === 'PGRST204') {
    console.warn('[Auto-promote] Some columns not found, filtering and retrying...');
    console.warn('[Auto-promote] Error message:', updateError.message);
    
    // Extract the missing column name from the error message
    const missingColumnMatch = updateError.message?.match(/Could not find the '(\w+)' column/);
    const missingColumn = missingColumnMatch ? missingColumnMatch[1] : null;
    
    if (missingColumn) {
      console.warn(`[Auto-promote] Missing column detected: ${missingColumn}`);
      // Remove the missing column from enhancements
      const { [missingColumn]: removed, ...enhancementsFiltered } = enhancements;
      
      // Also proactively remove brief_description, contact_phone, and contact_email if they exist, 
      // since they may not exist in DB
      if (enhancementsFiltered.brief_description && missingColumn !== 'brief_description') {
        delete enhancementsFiltered.brief_description;
        console.warn('[Auto-promote] Proactively removing brief_description to avoid future errors');
      }
      if (enhancementsFiltered.contact_phone && missingColumn !== 'contact_phone') {
        delete enhancementsFiltered.contact_phone;
        console.warn('[Auto-promote] Proactively removing contact_phone to avoid future errors');
      }
      if (enhancementsFiltered.contact_email && missingColumn !== 'contact_email') {
        delete enhancementsFiltered.contact_email;
        console.warn('[Auto-promote] Proactively removing contact_email to avoid future errors');
      }
      
      const updateFiltered = {
        ...safeUpdateFields,
        ...enhancementsFiltered
      };
      
      const { error: retryError } = await supabase
        .from('camp_audit_data')
        .update(updateFiltered)
        .eq('id', campAuditId);
      
      if (retryError) {
        // If still failing, try recursively removing more columns
        if (retryError.code === 'PGRST204') {
          console.warn('[Auto-promote] Multiple columns missing, trying with only user_* fields...');
          
          // Extract the new missing column name
          const newMissingColumnMatch = retryError.message?.match(/Could not find the '(\w+)' column/);
          const newMissingColumn = newMissingColumnMatch ? newMissingColumnMatch[1] : null;
          
          // Filter to only include fields that are likely to exist
          // Remove any fields that were already identified as missing
          const userFieldsOnly = Object.keys(enhancementsFiltered)
            .filter(key => {
              // Skip columns that were identified as missing
              if (key === missingColumn || key === newMissingColumn) return false;
              // Always skip brief_description since it may not exist
              if (key === 'brief_description') return false;
              
              // Always skip contact_phone and contact_email if they don't exist in the database
              if (key === 'contact_phone' || key === 'contact_email') return false;
              
              // Include user_* fields and known safe fields
              return key.startsWith('user_') || 
                     key === 'refund_policy' || 
                     key === 'dates_confidence' || 
                     key === 'cost_confidence' || 
                     key === 'schedule_confidence' ||
                     key === 'registration_open_date' ||
                     key === 'early_registration_open_date' ||
                     key === 'registration_deadline' ||
                     key === 'early_bird_deadline' ||
                     key === 'cancellation_deadline' ||
                     key === 'organization' ||
                     key === 'editorial_summary' ||
                     key === 'website_url'; // Only editorial_summary, not brief_description
            })
            .reduce((obj, key) => {
              obj[key] = enhancementsFiltered[key];
              return obj;
            }, {});
          
          const updateUserFieldsOnly = {
            ...safeUpdateFields,
            ...userFieldsOnly
          };
          
          const { error: finalError } = await supabase
            .from('camp_audit_data')
            .update(updateUserFieldsOnly)
            .eq('id', campAuditId);
          
          if (finalError) {
            console.error('[Auto-promote] DEBUG - Update error (final retry):', finalError);
            throw finalError;
          } else {
            console.log('[Auto-promote] DEBUG - Update successful (user fields only)');
          }
        } else {
          console.error('[Auto-promote] DEBUG - Update error (retry):', retryError);
          throw retryError;
        }
      } else {
        console.log('[Auto-promote] DEBUG - Update successful (without missing column)');
      }
    } else {
      console.error('[Auto-promote] DEBUG - Update error (could not parse missing column):', updateError);
      throw updateError;
    }
  } else if (updateError) {
    console.error('[Auto-promote] DEBUG - Update error:', updateError);
    throw updateError;
  } else {
    console.log('[Auto-promote] DEBUG - Update successful');
  }
  
  // Track individual contributions (check for duplicates first)
  if (contributions.length > 0) {
    for (const contribution of contributions) {
      // Check if this contribution already exists
      const { data: existing } = await supabase
        .from('camp_contributions')
        .select('id')
        .eq('camp_audit_id', contribution.camp_audit_id)
        .eq('user_id', contribution.user_id)
        .eq('field_name', contribution.field_name)
        .maybeSingle();
      
      if (existing) {
        // Update existing contribution
        const { error: updateError } = await supabase
          .from('camp_contributions')
          .update({
            new_value: contribution.new_value,
            contribution_source: contribution.contribution_source,
            confidence_score: contribution.confidence_score,
            applied: true,
            applied_at: new Date().toISOString()
          })
          .eq('id', existing.id);
        
        if (updateError) {
          console.warn('[Contributions] Failed to update:', updateError);
        }
      } else {
        // Insert new contribution
        const { error: insertError } = await supabase
          .from('camp_contributions')
          .insert({
            camp_audit_id: contribution.camp_audit_id,
            user_id: contribution.user_id,
            field_name: contribution.field_name,
            new_value: contribution.new_value,
            contribution_source: contribution.contribution_source,
            confidence_score: contribution.confidence_score,
            applied: true,
            applied_at: new Date().toISOString()
          });
        
        if (insertError) {
          console.warn('[Contributions] Failed to insert:', insertError);
        }
      }
    }
  }
  
  return {
    campId: campAuditId,
    enhancementsApplied: Object.keys(enhancements).length,
    contributionsTracked: contributions.length
  };
}

/**
 * Create new camp from user-contributed data
 */
async function createCampFromSanitizedData(sanitizedData, userId, userProgram) {
  // Parse schedule for structured data
  const parsedSchedule = sanitizedData.schedule_text 
    ? parseScheduleText(sanitizedData.schedule_text)
    : null;
  
  const newCampData = {
    // Generate audit_id
    audit_id: `user_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    
    // Core fields from sanitized data
    name: sanitizedData.name,
    location: sanitizedData.location,
    coordinates_lat: sanitizedData.latitude,
    coordinates_lng: sanitizedData.longitude,
    website: sanitizedData.website,
    phone: sanitizedData.phone,
    
    // Source tracking
    user_contributed: true,
    contributed_by: userId,
    contribution_type: 'full_entry',
    original_source: userProgram.source || 'manual_entry',
    source: 'user_contributed',
    
    // User-enhanced fields
    user_camp_start_date: sanitizedData.schedule_dates?.start,
    user_camp_end_date: sanitizedData.schedule_dates?.end,
    registration_open_date: sanitizedData.registration_open_date,
    early_registration_open_date: sanitizedData.early_registration_open_date,
    registration_deadline: sanitizedData.registration_deadline,
    early_bird_deadline: sanitizedData.early_bird_deadline,
    cancellation_deadline: sanitizedData.cancellation_deadline,
    user_cost_details: sanitizedData.price ? {
      amount: sanitizedData.price,
      per: sanitizedData.price_period,
      notes: sanitizedData.price_notes,
      reported_date: new Date().toISOString()
    } : null,
    user_schedule_details: parsedSchedule,
    user_session_start_time: sanitizedData.session_start_time || parsedSchedule?.drop_off,
    user_session_end_time: sanitizedData.session_end_time || parsedSchedule?.pick_up,
    user_activities: sanitizedData.activities,
    user_age_range_verified: sanitizedData.age_range,
    refund_policy: sanitizedData.refund_policy,
    contact_email: sanitizedData.email,
    contact_phone: sanitizedData.phone,
    website_url: sanitizedData.website,
    
    // Community metadata
    has_community_enhancements: true,
    community_contribution_count: 1,
    last_community_update: new Date().toISOString(),
    
    // Confidence scores (user-contributed starts lower than Google)
    data_confidence_score: 65,
    cost_confidence: sanitizedData.price ? 'medium' : null,
    schedule_confidence: parsedSchedule ? 'medium' : null,
    dates_confidence: sanitizedData.schedule_dates ? 'medium' : null,
    
    // Auto-approve (no moderation queue)
    moderation_status: 'approved',
    
    // Timestamps
    created_at: new Date().toISOString(),
    audit_date: new Date().toISOString(),
    last_fetched_from_google: null // Not from Google
  };
  
  // Insert into camp_audit_data
  // Try with all fields first, but filter out contact_phone and contact_email if they don't exist
  let { data: newCamp, error: insertError } = await supabase
    .from('camp_audit_data')
    .insert(newCampData)
    .select()
    .single();
  
  // If insert failed due to missing columns, retry without contact_phone and contact_email
  if (insertError && insertError.code === 'PGRST204') {
    console.warn('[Auto-promote] Some columns not found during insert, filtering and retrying...');
    console.warn('[Auto-promote] Error message:', insertError.message);
    
    // Extract the missing column name from the error message
    const missingColumnMatch = insertError.message?.match(/Could not find the '(\w+)' column/);
    const missingColumn = missingColumnMatch ? missingColumnMatch[1] : null;
    
    if (missingColumn) {
      console.warn(`[Auto-promote] Missing column detected during insert: ${missingColumn}`);
      
      // Remove the missing column from newCampData
      const { [missingColumn]: removed, ...campDataFiltered } = newCampData;
      
      // Also proactively remove contact_phone and contact_email if they exist
      if (campDataFiltered.contact_phone && missingColumn !== 'contact_phone') {
        delete campDataFiltered.contact_phone;
        console.warn('[Auto-promote] Proactively removing contact_phone from insert');
      }
      if (campDataFiltered.contact_email && missingColumn !== 'contact_email') {
        delete campDataFiltered.contact_email;
        console.warn('[Auto-promote] Proactively removing contact_email from insert');
      }
      
      // Retry insert without the missing column
      const retryResult = await supabase
        .from('camp_audit_data')
        .insert(campDataFiltered)
        .select()
        .single();
      
      if (retryResult.error) {
        // If still failing, try removing both contact columns
        if (retryResult.error.code === 'PGRST204') {
          const { contact_phone: removed1, contact_email: removed2, ...campDataFinal } = campDataFiltered;
          console.warn('[Auto-promote] Removing both contact_phone and contact_email from insert');
          
          const finalResult = await supabase
            .from('camp_audit_data')
            .insert(campDataFinal)
            .select()
            .single();
          
          if (finalResult.error) {
            console.error('[Auto-promote] Insert error (final retry):', finalResult.error);
            throw finalResult.error;
          }
          
          newCamp = finalResult.data;
        } else {
          console.error('[Auto-promote] Insert error (retry):', retryResult.error);
          throw retryResult.error;
        }
      } else {
        newCamp = retryResult.data;
        console.log('[Auto-promote] Insert successful (without missing column)');
      }
    } else {
      console.error('[Auto-promote] Insert error (could not parse missing column):', insertError);
      throw insertError;
    }
  } else if (insertError) {
    console.error('[Auto-promote] Insert error:', insertError);
    throw insertError;
  } else {
    console.log('[Auto-promote] Insert successful');
  }
  
  // Track contribution
  const contributions = [];
  
  if (sanitizedData.schedule_dates) {
    contributions.push({
      camp_audit_id: newCamp.id,
      user_id: userId,
      field_name: 'camp_dates',
      new_value: sanitizedData.schedule_dates,
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 70,
      applied: true,
      applied_at: new Date().toISOString()
    });
  }
  
  if (sanitizedData.price) {
    contributions.push({
      camp_audit_id: newCamp.id,
      user_id: userId,
      field_name: 'cost',
      new_value: { amount: sanitizedData.price, per: sanitizedData.price_period },
      contribution_source: userProgram.source || 'manual_entry',
      confidence_score: 75,
      applied: true,
      applied_at: new Date().toISOString()
    });
  }
  
  if (contributions.length > 0) {
    await supabase.from('camp_contributions').insert(contributions);
  }
  
  return {
    campId: newCamp.id,
    camp: newCamp
  };
}

/**
 * Batch promote all user programs for a user
 * Useful for retroactive sync when user first enables sharing
 */
export async function batchPromoteUserPrograms(userId) {
  const { getTrackedPrograms } = await import('@/lib/database/tracked-programs-helpers');
  
  const programs = await getTrackedPrograms(userId);
  
  // Filter programs that haven't been promoted yet
  // (check program_data for promotion status)
  const unpromotedPrograms = programs.filter(p => 
    !p.program_data?.promoted_to_audit_id && 
    !p.program_data?.auto_shared_with_community
  );
  
  const results = {
    total: unpromotedPrograms.length,
    succeeded: 0,
    failed: 0,
    errors: []
  };
  
  if (unpromotedPrograms.length === 0) {
    return results;
  }
  
  for (const program of unpromotedPrograms) {
    try {
      await autoPromoteUserProgramToCommunity(program.id, userId);
      results.succeeded++;
    } catch (error) {
      results.failed++;
      results.errors.push({
        programId: program.id,
        error: error.message
      });
    }
  }
  
  return results;
}

